<template>
  <v-alert
    v-if="filters.dates.to && filters.dates.from !== filters.dates.to"
    color="error"
    type="error"
    dark
    class="mt-2"
  >
    {{ $t("daily_report") }}
  </v-alert>
  <div v-else :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="end" class="mb-4">
      <v-col cols="8" lg="auto">
        <report-field-select-food-company></report-field-select-food-company>
      </v-col>
      <v-col cols="4" lg="auto">
        <shift-picker
          v-model="selectedShift"
          :disabled="!selectedCompany"
          :items="shifts"
          :label="$t('shift.shift')"
        />
      </v-col>
      <v-col align="end">
        <p class="mb-0 text-right" v-if="!loading">
          {{ companyDailyOrders.length }} {{ $t("results") }}
        </p>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      <p class="mt-4 text-body-1">{{ $t("loading") }}...</p>
    </div>

    <!-- Content -->
    <v-row v-else id="user-daily-orders" class="mt-0">
      <v-col
        v-for="(user, i) in companyDailyOrders"
        :key="user.id"
        cols="12"
        md="6"
        lg="4"
        xl="3"
      >
        <v-card class="fill-height ordera-box-shadow user-daily-orders">
          <v-card-title>
            <v-spacer></v-spacer>
            <small class="grey--text">#{{ user.id }}&nbsp;</small>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ toLatin(user.name) }} {{ toLatin(user.lastname) }}
                </span>
              </template>
              <span>{{ user.userId }}</span>
            </v-tooltip>
            <v-spacer></v-spacer>
          </v-card-title>
          <v-card-subtitle></v-card-subtitle>
          <v-list dense>
            <v-list-item-group
              v-for="(category, i) in mealCategories"
              v-if="Array.isArray(user[category]) && user[category].length"
              :key="i"
            >
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    <template v-for="meal in user[category]">
                      <strong>{{ meal.quantity }}x</strong>
                      {{ toLatin(meal.name) }}
                    </template>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- No Data State -->
    <div v-if="!loading && companyDailyOrders.length === 0" class="text-center py-8">
      <v-icon size="64" color="grey lighten-1">mdi-food-off</v-icon>
      <p class="mt-4 text-body-1 grey--text">{{ $t("no_orders_found") }}</p>
    </div>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasFoodOrders from "@/mixins/report/hasFoodOrders";
import hasPrinting from "@/mixins/hasPrinting";
import ReportFieldSelectFoodCompany from "@/components/report/fields/ReportFieldSelectFoodCompany.vue";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasReloadWatch from "@/mixins/report/hasReloadWatch";

export default {
  name: "ReportTabGroupMealsTabOrders",
  mixins: [hasFoodOrders, hasPrinting, hasReportPrinting, hasExcel, hasReloadWatch],
  components: {
    ReportFieldSelectFoodCompany,
  },
  data() {
    return {
      users: [],
      orders: [],
      loading: false,
    };
  },
  methods: {
    findUsers() {
      if (!this.selectedCompany) {
        this.users = [];
        this.loading = false;
        return;
      }

      this.loading = true;
      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
      };
      reportService
        .getFoodOrdersOrdered(this.$store.state.restaurant.id, params)
        .then((response) => {
          this.setMealCategories(response.data);
          this.setShifts(response.data);
          this.orders = response.data;
        })
        .catch((error) => {
          console.error("Error loading orders:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    userOrders() {
      return [...new Set((this.orders || []).map((item) => item.user_id))]
        .map((userId) => {
          const userOrders = this.orders.filter(
            (item) => item.user_id === userId && item.shift === this.selectedShift
          );
          if (!userOrders.length) return null;
          const dailyOrders = {
            userId,
            company_id: userOrders[0].company_id,
            name: userOrders[0].name,
            lastname: userOrders[0].lastname,
            id: userOrders[0].id,
          };
          this.mealCategories.forEach((category) => {
            dailyOrders[category] = this.getDailyDish(userOrders, category);
          });
          return dailyOrders;
        })
        .filter(Boolean);
    },
    selectedCompany() {
      return this.$store.state.report.selectedCompany;
    },
    companyDailyOrders() {
      let orders = this.userOrders.filter(
        (order) => order.company_id === this.selectedCompany?.id
      );

      if (this.filters.search && this.filters.search.trim() !== "") {
        const searchTerm = this.translate(this.filters.search.toLowerCase().trim());
        orders = orders.filter((user) => {
          const fullName = `${this.translate(user.name)} ${this.translate(
            user.lastname
          )}`.toLowerCase();
          if (fullName.includes(searchTerm)) {
            return true;
          }

          // Check if any meal label contains the search term
          return this.mealCategories.some((category) => {
            if (Array.isArray(user[category]) && user[category].length) {
              return user[category].some((meal) =>
                this.translate(meal.label).toLowerCase().includes(searchTerm)
              );
            }
            return false;
          });
        });
      }

      return orders.map((user) => {
        return {
          ...user,
          name: this.translate(user.name),
          lastname: this.translate(user.lastname),
        };
      });
    },
    headers() {
      return [
        { text: this.$t("name"), value: "name" },
        { text: this.$t("last_name"), value: "lastname" },
        ...this.mealCategories.map((category) => ({
          text: this.translate(category),
          value: category,
        })),
      ];
    },
    translatedMealCategories() {
      return this.mealCategories.map((category) => ({
        text: this.translate(category),
        value: category,
      }));
    },
    printItems() {
      return this.userOrders.map((user) => {
        const userData = {
          name: user.name,
          lastname: user.lastname,
          id: user.id,
        };
        this.mealCategories.forEach((category) => {
          userData[category] = user[category].map((meal) => meal.label).join(", ");
        });
        return userData;
      });
    },
    filters() {
      return this.$store.state.report.filters;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.findUsers();
      },
      deep: true,
    },
    selectedCompany: {
      handler() {
        this.findUsers();
      },
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        let appendHtml = `<div style='display: flex;flex-wrap: wrap;'>`;
        this.companyDailyOrders.forEach((user) => {
          appendHtml += `<div style='flex: 0 0 50%;box-sizing: border-box;padding: 12px;border: 1px solid #ccc;'>
          <h3 style="text-align:center;margin-top: 0"><small style="color: #999">#${user.id}</small> ${user.name} ${user.lastname}</h3>`;
          this.mealCategories.forEach((category) => {
            if (Array.isArray(user[category]) && user[category].length) {
              appendHtml += `<p>${this.translate(category)}: ${user[category]
                .map((meal) => this.translate(meal.label))
                .join(", ")}</p>`;
            }
          });
          appendHtml += `</div>`;
        });
        appendHtml += `</div>`;
        const title = `<h3 style="text-align: center">
            ${
              this.toLatin(this.selectedCompany?.company || this.selectedCompany?.name) +
              " " +
              this.subtitle
            }
          </h3>`;
        this.printDocument({
          title: [this.$t("report"), this.$t("orders")].join(": "),
          afterTableHtml: appendHtml,
          beforeTableHtml: title,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const orders = this.companyDailyOrders.map((order) => {
          this.mealCategories.forEach((category) => {
            if (Array.isArray(order[category]) && order[category].length) {
              order[category] = order[category]
                .map((meal) => {
                  return this.translate(meal.label);
                })
                .join(", ");
            } else {
              order[category] = "";
            }
          });
          return order;
        });
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("orders")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("orders"),
              headers: this.headers,
              items: orders,
              title: [this.$t("report"), this.$t("orders")].join(" "),
              subtitle: this.subtitle,
              beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompany?.company}</h4>`,
            },
          ],
        });
      }
    },
  },
};
</script>
